import * as React from 'react';
import { useNavigate } from 'react-router';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MuiMenuItem from '@mui/material/MenuItem';
import { Divider } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Settings from '@mui/icons-material/Settings';
import SettingsRemote from '@mui/icons-material/SettingsRemote';
import RuleIcon from '@mui/icons-material/Rule';
import MenuItemHiddenTextLabel from '../common/MenuItemHiddenTextLabel';
import GlyntTagsEditDialog from './GlyntTagsEditDialog';
import RouterConfigSettings from './RouterConfigSettings';
import { IDataPool } from '../../types/my-types';
import { featureFlags } from '../../utils/featureFlags';

interface DataPoolActionsProps {
  dataPool: IDataPool;
  dataPoolNames: string[];
}

const DataPoolActions: React.FC<DataPoolActionsProps> = ({ dataPool, dataPoolNames }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [routerConfigOpen, setRouterConfigOpen] = React.useState(false);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleRulesetsClick = () => {
    handleMenuClose();
    navigate(`/data-pools/${dataPool.id}/rulesets`);
  };

  return (
    <div>
      <IconButton
        aria-label="more"
        aria-controls={menuOpen ? 'datapool-actions-menu' : undefined}
        aria-expanded={menuOpen ? 'true' : undefined}
        aria-haspopup="true"
        onClick={handleMenuClick}
        data-cy={`btn-datapool-actions-${dataPool.label}`}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        id="datapool-actions-menu"
        MenuListProps={{ 'aria-labelledby': 'datapool-actions-button' }}
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
      >
        <MuiMenuItem
          data-cy={`btn-edit-settings-${dataPool.label}`}
          onClick={() => {
            setSettingsOpen(true);
            handleMenuClose();
          }}
          sx={{ fontSize: '0.875rem', padding: '4px 8px' }}
        >
          <ListItemIcon>
            <Settings fontSize="small" />
          </ListItemIcon>
          <MenuItemHiddenTextLabel>Settings</MenuItemHiddenTextLabel>
        </MuiMenuItem>
        {featureFlags.dataPoolRouterConfigSettings ? (
          <>
            <Divider />
            <MuiMenuItem
              data-cy={`btn-datapool-router-config-${dataPool.label}`}
              onClick={() => {
                setRouterConfigOpen(true);
                handleMenuClose();
              }}
              sx={{ fontSize: '0.875rem', padding: '4px 8px' }}
            >
              <ListItemIcon>
                <SettingsRemote fontSize="small" />
              </ListItemIcon>
              <MenuItemHiddenTextLabel>Router Config</MenuItemHiddenTextLabel>
            </MuiMenuItem>
          </>
        ) : null}
        {featureFlags.rulesets ? (
          <>
            <Divider />
            <MuiMenuItem
              data-cy={`btn-datapool-rulesets-${dataPool.label}`}
              onClick={handleRulesetsClick}
              sx={{ fontSize: '0.875rem', padding: '4px 8px' }}
            >
              <ListItemIcon>
                <RuleIcon fontSize="small" />
              </ListItemIcon>
              <MenuItemHiddenTextLabel>Rulesets</MenuItemHiddenTextLabel>
            </MuiMenuItem>
          </>
        ) : null}
      </Menu>
      {settingsOpen ? (
        <GlyntTagsEditDialog
          dataPool={dataPool}
          dataPoolNames={dataPoolNames}
          open={settingsOpen}
          onDialogClose={() => setSettingsOpen(false)}
        />
      ) : null}
      {featureFlags.dataPoolRouterConfigSettings ? (
        <RouterConfigSettings
          dataPool={dataPool}
          open={routerConfigOpen}
          onDialogClose={() => setRouterConfigOpen(false)}
        />
      ) : null}
    </div>
  );
};

export default DataPoolActions;
